import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="container-custom py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-8 transition-colors"
          data-testid="back-to-home-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al inicio
        </Link>

        <article className="prose prose-slate max-w-4xl mx-auto">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-8">
            Política de Privacidad
          </h1>

          <p className="text-slate-600 mb-4">
            <strong>Última actualización:</strong> Agosto 2026
          </p>

          <p className="text-slate-600 leading-relaxed">
            En <strong>Apps Innovation SAS</strong>, operadores de <strong>Mi Barrio App</strong>, nos comprometemos a proteger la privacidad de nuestros usuarios: Clientes, Tiendas y Repartidores. Esta Política de Privacidad describe qué información recopilamos, cómo la usamos, con quién la compartimos y cómo la protegemos.
          </p>

          <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10 mb-4">
            1. Información que Recopilamos
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Dependiendo de tu rol en la plataforma (Cliente, Tienda o Repartidor), podemos recopilar:
          </p>
          <ul className="text-slate-600 space-y-2 ml-6 list-disc">
            <li><strong>Información de contacto:</strong> Nombre, dirección de correo electrónico, número de teléfono.</li>
            <li><strong>Información de la cuenta:</strong> Datos de registro, documento de identidad, y para Tiendas, información del negocio (razón social, dirección, cuenta bancaria).</li>
            <li><strong>Datos de ubicación (GPS):</strong> Ver la sección dedicada más abajo.</li>
            <li><strong>Fotos e imágenes:</strong> Ver la sección dedicada más abajo.</li>
            <li><strong>Datos biométricos (solo Repartidores):</strong> Ver la sección dedicada más abajo.</li>
            <li><strong>Información de pedidos y pagos:</strong> Historial de pedidos, montos, y datos procesados por nuestra pasarela de pagos.</li>
            <li><strong>Información técnica:</strong> Dirección IP, tipo de dispositivo y sistema operativo, para fines de seguridad y soporte.</li>
          </ul>

          <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10 mb-4">
            2. Uso de Datos de Ubicación
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Mi Barrio App utiliza tu ubicación geográfica para las siguientes funciones esenciales del servicio:
          </p>
          <ul className="text-slate-600 space-y-2 ml-6 list-disc">
            <li>Mostrar tiendas y comercios cercanos a tu ubicación.</li>
            <li>Calcular rutas y tiempos estimados de entrega.</li>
            <li>Permitir a los Repartidores recibir pedidos cercanos y completar entregas, incluyendo el <strong>seguimiento de ubicación en segundo plano</strong> mientras una entrega está activa.</li>
            <li>Mostrar al Cliente, en tiempo real, el progreso y la llegada de su pedido en el mapa.</li>
          </ul>
          <p className="text-slate-600 leading-relaxed">
            Para los Repartidores, el permiso de ubicación en segundo plano es obligatorio para operar: sin él, no es posible recibir ni completar pedidos. Tu ubicación en tiempo real solo se comparte con el Cliente y la Tienda mientras tengas un pedido activo asignado, nunca de forma permanente ni con terceros ajenos al servicio.
          </p>

          <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10 mb-4">
            3. Fotos, Cámara y Documentos
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Usamos el acceso a la cámara o galería de tu dispositivo para:
          </p>
          <ul className="text-slate-600 space-y-2 ml-6 list-disc">
            <li>Permitir que las Tiendas suban fotografías de sus productos para el catálogo.</li>
            <li>Permitir que Tiendas y Repartidores suban documentos de identidad o de registro comercial durante el proceso de verificación de cuenta.</li>
            <li>Permitir que los Repartidores tomen una foto como evidencia de entrega cuando el Cliente no se encuentra presente.</li>
          </ul>

          <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10 mb-4">
            4. Datos Biométricos (Verificación de Repartidores)
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Para verificar la identidad de las personas que se registran como Repartidores, Mi Barrio App utiliza tecnología de reconocimiento facial que compara una imagen del rostro del usuario con su documento de identidad. Este procesamiento se realiza únicamente durante el proceso de verificación de la cuenta y tiene como único propósito confirmar que la persona registrada corresponde a la titular del documento presentado. No utilizamos esta información con fines distintos ni la compartimos con terceros ajenos al proceso de verificación.
          </p>

          <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10 mb-4">
            5. Pagos
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Los pagos realizados dentro de la aplicación son procesados por <strong>Wompi</strong>, nuestra pasarela de pagos autorizada. No almacenamos los datos completos de tu tarjeta o cuenta bancaria en nuestros servidores; esta información es gestionada y tokenizada de forma segura directamente por Wompi conforme a sus propios estándares de seguridad.
          </p>

          <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10 mb-4">
            6. Uso de la Información
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Utilizamos la información recopilada para:
          </p>
          <ul className="text-slate-600 space-y-2 ml-6 list-disc">
            <li>Crear, administrar y verificar tu cuenta.</li>
            <li>Procesar, coordinar y hacer seguimiento a tus pedidos.</li>
            <li>Procesar pagos y liquidar ganancias a Tiendas y Repartidores.</li>
            <li>Brindar soporte y responder a tus consultas.</li>
            <li>Cumplir con obligaciones legales, contables y regulatorias.</li>
            <li>Mejorar la seguridad y funcionamiento de la plataforma.</li>
          </ul>
          <p className="text-slate-600 leading-relaxed">No vendemos tu información personal.</p>

          <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10 mb-4">
            7. Con Quién Compartimos Información
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Compartimos información únicamente cuando es necesario para operar el servicio:
          </p>
          <ul className="text-slate-600 space-y-2 ml-6 list-disc">
            <li><strong>Entre Cliente, Tienda y Repartidor:</strong> nombre, teléfono, dirección y ubicación del pedido, solo durante un pedido activo.</li>
            <li><strong>Con Wompi:</strong> los datos necesarios para procesar el pago.</li>
            <li><strong>Con autoridades:</strong> cuando sea legalmente requerido, o para investigar fraude o proteger la seguridad de los usuarios.</li>
            <li>Con tu consentimiento explícito en cualquier otro caso.</li>
          </ul>

          <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10 mb-4">
            8. Seguridad de los Datos
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Implementamos medidas de seguridad técnicas y organizativas apropiadas para proteger tu información contra acceso no autorizado, alteración, divulgación o destrucción. Ningún método de transmisión por Internet es 100% seguro, pero trabajamos activamente para minimizar los riesgos.
          </p>

          <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10 mb-4">
            9. Retención de Datos
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Conservamos tu información personal mientras tu cuenta esté activa y durante el tiempo necesario para cumplir los fines para los que fue recopilada, incluyendo requisitos legales, contables o de auditoría.
          </p>

          <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10 mb-4">
            10. Eliminación de tu Cuenta
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Puedes eliminar tu cuenta directamente desde la aplicación, en la sección Cuenta. Al hacerlo, se eliminan tus datos personales y sensibles; se conserva únicamente la información necesaria para fines de auditoría legal y contable (órdenes, calificaciones y transacciones), de forma disociada de tus datos personales. Más detalles en{" "}
            <Link to="/eliminar-cuenta" className="text-blue-600 hover:text-blue-700 underline">
              esta página
            </Link>.
          </p>

          <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10 mb-4">
            11. Tus Derechos (Ley 1581 de 2012 - Colombia)
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Como titular de tus datos, tienes derecho a:
          </p>
          <ul className="text-slate-600 space-y-2 ml-6 list-disc">
            <li>Conocer, actualizar y rectificar tu información.</li>
            <li>Solicitar la eliminación de tus datos cuando no exista obligación legal de conservarlos.</li>
            <li>Revocar tu consentimiento en cualquier momento.</li>
            <li>Presentar quejas ante la autoridad competente.</li>
          </ul>

          <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10 mb-4">
            12. Cookies
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Nuestro sitio web puede utilizar cookies para mejorar tu experiencia de navegación. Puedes configurar tu navegador para rechazarlas, aunque esto puede afectar algunas funcionalidades.
          </p>

          <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10 mb-4">
            13. Cambios en esta Política
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Nos reservamos el derecho de actualizar esta Política de Privacidad. Los cambios serán publicados en esta página con la fecha de actualización correspondiente y, cuando sea significativo, notificados dentro de la aplicación.
          </p>

          <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10 mb-4">
            14. Contacto
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Si tienes preguntas sobre esta Política de Privacidad o sobre cómo manejamos tu información personal, puedes contactarnos a través de:
          </p>
          <ul className="text-slate-600 space-y-2 ml-6 list-disc">
            <li><strong>Empresa:</strong> Apps Innovation SAS</li>
            <li><strong>Correo:</strong> mibarrioapp151@gmail.com</li>
            <li><strong>Teléfono:</strong> 310 8864437</li>
          </ul>

          <div className="mt-12 pt-8 border-t border-slate-200">
            <p className="text-slate-500 text-sm">
              © 2026 Apps Innovation SAS. Todos los derechos reservados.
            </p>
          </div>
        </article>
      </div>
    </div>
  );
};

export default PrivacyPolicy;